/**
 * Shared password policy for reset / change-password flows across portals.
 * Requires: min 8 chars, 1 uppercase, 1 number, 1 symbol.
 */

export type PasswordRequirementKey = 'minLength' | 'uppercase' | 'number' | 'symbol';

export type PasswordRequirements = Record<PasswordRequirementKey, boolean>;

export type PasswordRequirementItem = {
  key: PasswordRequirementKey;
  ok: boolean;
  label: string;
};

export const PASSWORD_POLICY_MESSAGE =
  'Password must be at least 8 characters and include at least one uppercase letter, one number, and one symbol';

export function getPasswordRequirements(password: string): PasswordRequirements {
  const value = password || '';
  return {
    minLength: value.length >= 8,
    uppercase: /[A-Z]/.test(value),
    number: /[0-9]/.test(value),
    symbol: /[^A-Za-z0-9]/.test(value),
  };
}

export function getPasswordRequirementItems(password: string): PasswordRequirementItem[] {
  const req = getPasswordRequirements(password);
  return [
    { key: 'minLength', ok: req.minLength, label: 'At least 8 characters' },
    { key: 'uppercase', ok: req.uppercase, label: 'One uppercase letter (A–Z)' },
    { key: 'number', ok: req.number, label: 'One number (0–9)' },
    { key: 'symbol', ok: req.symbol, label: 'One symbol (e.g. !@#$%)' },
  ];
}

export function isPasswordStrong(password: string): boolean {
  const req = getPasswordRequirements(password);
  return req.minLength && req.uppercase && req.number && req.symbol;
}

export function assertPasswordStrong(password: string): void {
  if (!isPasswordStrong(password)) {
    throw new Error(PASSWORD_POLICY_MESSAGE);
  }
}
